import React, { useRef, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import { useInvokeDenaliAssistant } from '../../../../api/denaliAssistant'
import MessageInput from '../common/MessageInput'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import Background from '../../../../providers/BackgroundProvider/Background'
import Greetings from './Greetings'
import SuggestionList from './SuggestionList'
import Notification from './ReadingListNotification'

const useStyles = makeStyles((theme) => ({
  landingContainer: {
    position: 'relative',
    maxWidth: '1000px',
    minWidth: '640px',
    height: '100%',
    margin: '0 auto',
    padding: '0 var(--padding-md)'
  }
}))

function LandingPage () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const messageInputRef = useRef(null)
  const isMountedRef = useRef(true)
  const [hasPrefilledPrompt, setHasPrefilledPrompt] = useState(false)
  const [focus, setFocus] = useState(null)

  // Mutations
  const invokeMutation = useInvokeDenaliAssistant()

  // Track mount status and prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Prevent React Query from updating state after unmount by using a stable mutation function
  const stableInvokeMutation = useCallback((params) => {
    if (!isMountedRef.current) {
      return Promise.resolve(null)
    }
    return invokeMutation.mutateAsync(params)
  }, [invokeMutation])

  // Pre-fill prompt from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const prompt = params.get('prompt')

    if (prompt && messageInputRef.current) {
      messageInputRef.current.setValue(prompt)
      setHasPrefilledPrompt(true)
    }

    const focusType = params.get('focusType')
    const focusSubjectIdentifier = params.get('focusSubjectIdentifier')
    const focusSubjectLabel = params.get('focusSubjectLabel')
    if (focusType && focusSubjectIdentifier && focusSubjectLabel) {
      setFocus({
        type: focusType,
        subjectIdentifier: focusSubjectIdentifier,
        subjectLabel: focusSubjectLabel
      })
    }
  }, [location.search])

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim() || !isMountedRef.current) return

    try {
      // Build context object, including focus if present
      const context = {
        currentView: 'ai-assistant-chat'
      }
      if (focus) {
        context.focus = focus
      }

      // Send the message - thread will be created automatically if needed
      const result = await stableInvokeMutation({
        prompt: message,
        attachments,
        context,
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        }
      })

      // Check if component is still mounted before proceeding
      if (!isMountedRef.current) {
        return
      }

      // If this was a new thread, update URL to the new thread
      // Use setTimeout to ensure mutation state updates complete before navigation
      if (result?.threadId) {
        setTimeout(() => {
          if (isMountedRef.current) {
            history.replace(`/denali-ai/chat/${result.threadId}`)
          }
        }, 0)
      }
    } catch (error) {
      // Only log if still mounted
      if (isMountedRef.current) {
        console.error('Failed to send message:', error)
        // TODO: Show error toast
      }
    }
  }, [stableInvokeMutation, history, focus])

  return (
    <Background
      // light='center / cover no-repeat url(https://resources.swsapp.com/100/images/purpleMountains.webp)'
      light='var(--dai-bg)'
      dark='var(--dai-bg)'
    >
      <div className={classes.landingContainer}>
        <FlexColumn className='justify-center items-center' style={{ width: '100%', height: 'calc(100% - 15vh)', gap: '48px' }}>
          <FadeIn className='w-full'>
            <Greetings
              greeting={focus ? `__, let's chat about ${focus.subjectLabel}...` : undefined}
            />
          </FadeIn>
          <FadeIn delay={0.1} className='w-full mb-5'>
            <MessageInput
              ref={messageInputRef}
              onSend={handleSendMessage}
              placeholder='What do you want to know?'
              minRows={4}
            />
          </FadeIn>
          {!hasPrefilledPrompt && (
            <FadeIn delay={1} duration={1}>
              <SuggestionList
                onSuggestionClick={(prompt) => {
                // Set the suggestion in the input so user can review/modify before sending
                  messageInputRef.current?.setValue(prompt)
                }}
              />
            </FadeIn>
          )}
        </FlexColumn>
        <Notification />
      </div>
    </Background>
  )
}

export default LandingPage
